import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { format, parseISO } from 'date-fns';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { CircularProgress } from '@material-ui/core';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import { ARCHIVE_CORP_DOCUMENT, GET_ORG_CONTRACTOR_PARTNERSHIPS } from '@/components/CorporateUsers/graphql';
import { linkedShippersByCarrierFrom } from '@/components/CorporateUsers/Carriers/utils';
import DocumentUploadModal from '@/components/CorporateUsers/modals/DocumentUploadModal';
import {
    AccentBar,
    Card,
    CardRow,
    EmptyState,
    Section,
    SectionHeader,
    SectionTitle,
    StatusPill,
} from '@/components/CorporateShipper/blocks';
import { REVIEW_CONTRACTOR_COMPLIANCE } from '@/constants/apiRoutes';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { greys, onwardNavy, reds } from '@/styles/colors';
import { post } from '@/utilities/onwardClient';
import { GET_CARRIER_COMPLIANCE_REVIEWS, GET_CARRIER_DOCUMENTS } from './graphql';
import ReviewDocumentModal from './ReviewDocumentModal';
import { AWAITING_REVIEW, DECISIONS, complianceHistoryRows, documentSlotsFor, latestDecisionByDocument } from './utils';

const OTHER_DOCUMENT_SLOT = { type: 'MISC', document: null };

const CardStrip = styled.div`
    align-items: center;
    background: #f9faf9;
    border-bottom: 1px solid ${greys.border};
    display: flex;
    gap: 8px;
    padding: 14px 24px;
`;

const StripTitle = styled.span`
    color: #3f3e4a;
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const StripNote = styled.span`
    color: ${greys.primary};
    font-size: 11.5px;
`;

const Row = styled(CardRow)`
    gap: 12px;
    padding: 16px 24px;

    & + & {
        border-top: 1px solid ${greys.border};
    }
`;

const RowTitle = styled.div`
    color: ${onwardNavy};
    font-size: 14.5px;
    font-weight: 800;
`;

const RowDetail = styled.div`
    color: ${({ alert }) => (alert ? reds.primary : greys.primary)};
    font-size: 12px;
    font-weight: ${({ alert }) => (alert ? 700 : 400)};
    margin-top: 6px;
`;

const RowActions = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: 12px;
`;

// On the admin's carrier page a row's right side is three groups: status, Compliance and Actions.
const StatusGroup = styled.div`
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ActionGroup = styled.div`
    border-left: 1px solid ${greys.border};
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-left: 16px;
`;

const GroupLabel = styled.span`
    color: ${greys.primary};
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const GroupButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const compactButton = css`
    min-width: auto;
    padding: 6px 20px;
    font-size: 13px;
`;

// Starting widths in px, each draggable from its header's right edge.
const HISTORY_COLUMNS = [
    { label: 'Date', width: 150 },
    { label: 'Document', width: 220 },
    { label: 'Decision', width: 160 },
    { label: 'Note', width: 320 },
    { label: 'Reviewed by', width: 200 },
];
const MIN_COLUMN_WIDTH = 80;

// Scrolls sideways when the columns are wider than the card.
const HistoryScroll = styled.div`
    overflow-x: auto;
`;

const HistoryGrid = styled.div`
    color: ${greys.primary};
    display: grid;
    font-size: 12.5px;
    gap: 12px;
    min-width: max-content;
    padding: 12px 24px;

    & + & {
        border-top: 1px solid ${greys.border};
    }
`;

// A cell cuts text that doesn't fit; its title shows the whole of it.
const HistoryCell = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const HeadCell = styled.span`
    position: relative;
`;

const ResizeHandle = styled.span`
    bottom: -12px;
    cursor: col-resize;
    position: absolute;
    right: -9px;
    top: -12px;
    touch-action: none;
    width: 6px;

    &:hover {
        background: ${greys.border};
    }
`;

const HistoryHead = styled(HistoryGrid)`
    background: #f9faf9;
    border-bottom: 1px solid ${greys.border};
    color: #3f3e4a;
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;

const toDisplayDate = (isoDate) => {
    const date = parseISO(isoDate);

    return format(date, 'MM/dd/yyyy');
};

const SectionHeading = ({ title }) => (
    <SectionHeader>
        <AccentBar />
        <SectionTitle>{title}</SectionTitle>
    </SectionHeader>
);

const ViewButton = ({ document }) => (
    <SecondaryButton css={compactButton} as="a" href={document.link} target="_blank" rel="noopener noreferrer">
        View
    </SecondaryButton>
);

const UploadButton = ({ slot, secondary, children }) => {
    const { client } = useContext(UserContext);
    const [open, setOpen] = useState(false);
    const Button = secondary ? SecondaryButton : PrimaryButton;

    return (
        <>
            <Button css={compactButton} onClick={() => setOpen(true)}>
                {children}
            </Button>
            {open && (
                <DocumentUploadModal
                    open
                    onClose={() => setOpen(false)}
                    type={slot.type}
                    client_id={client.client_id}
                    partner_client_id={slot.partnerClientId ?? null}
                    existingDoc={slot.document}
                    contextLabel={slot.shipperName}
                />
            )}
        </>
    );
};

const RemoveButton = ({ document }) => {
    const { callbacks } = useContext(CorpAdminContext);
    const [archiveDocument, { loading: removing }] = useMutation(ARCHIVE_CORP_DOCUMENT);

    const removeDocument = async () => {
        try {
            await archiveDocument({ variables: { document_id: document.document_id } });
            await callbacks.refetchCorpDocs();
        } catch (err) {
            Sentry.captureException(err);
            callbacks.setNotification({ severity: 'error', message: 'Error removing document' });
            return;
        }

        callbacks.setNotification({ severity: 'success', message: 'Document removed' });
    };

    return (
        <SecondaryButton css={compactButton} disabled={removing} onClick={removeDocument}>
            Remove
        </SecondaryButton>
    );
};

/**
 * Approve / Deny on the slot's document, each through a note dialog. `compliance` is the page's review state (see
 * InsuranceContracts); `latest` is the document's latest decision, whose button is disabled.
 */
const ReviewActions = ({ slot, title, compliance, latest }) => {
    const [decision, setDecision] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async (note) => {
        setSaving(true);
        const saved = await compliance.review({ document: slot.document, status: decision, note });
        setSaving(false);
        if (saved) setDecision(null);
    };

    return (
        <>
            {Object.entries(DECISIONS).map(([status, { verb }]) => (
                <SecondaryButton
                    key={status}
                    css={compactButton}
                    disabled={latest?.status === status}
                    onClick={() => setDecision(status)}
                >
                    {verb}
                </SecondaryButton>
            ))}
            {decision && (
                <ReviewDocumentModal
                    title={title}
                    decision={decision}
                    saving={saving}
                    onSubmit={submit}
                    onClose={() => setDecision(null)}
                />
            )}
        </>
    );
};

const SlotRow = ({ slot, compliance }) => {
    const { document } = slot;
    const title = slot.shipperName ? `${slot.label} — ${slot.shipperName}` : slot.label;

    const term = document?.term_months ? `${document.term_months}-month` : null;
    const signedOn = document?.signed_on ? toDisplayDate(document.signed_on) : null;
    const expiresOn = document?.expiration_date ? toDisplayDate(document.expiration_date) : null;
    let contractDetail = term ?? signedOn;
    if (term && signedOn) contractDetail = `${term} · ${signedOn}`;

    let detailText = '● not on file — required';
    if (slot.status === 'expired') detailText = `● expired ${expiresOn} — required`;
    if (slot.onFile) detailText = expiresOn ? `Expires ${expiresOn}` : contractDetail;

    let pill = null;
    if (slot.status === 'valid') pill = <StatusPill tone="on">On file</StatusPill>;
    if (slot.status === 'expiring') pill = <StatusPill>Expires in {slot.daysToExpiry}d</StatusPill>;

    const documentActions = (
        <>
            {document && <ViewButton document={document} />}
            <UploadButton slot={slot}>{document ? 'Replace' : 'Upload'}</UploadButton>
            {document && <RemoveButton document={document} />}
        </>
    );

    const heading = (
        <div>
            <RowTitle>{title}</RowTitle>
            {detailText && <RowDetail alert={!slot.onFile}>{detailText}</RowDetail>}
        </div>
    );

    if (!compliance) {
        return (
            <Row>
                {heading}
                <RowActions>
                    {pill}
                    {documentActions}
                </RowActions>
            </Row>
        );
    }

    const latest = document ? compliance.decisionsByDocument[document.document_id] : null;
    const decisionPill = DECISIONS[latest?.status] || AWAITING_REVIEW;

    return (
        <Row>
            {heading}
            <RowActions
                css={css`
                    align-items: stretch;
                    gap: 16px;
                `}
            >
                {(pill || document) && (
                    <StatusGroup>
                        {pill}
                        {document && <StatusPill tone={decisionPill.tone}>{decisionPill.label}</StatusPill>}
                    </StatusGroup>
                )}
                {document && (
                    <ActionGroup>
                        <GroupLabel>Compliance</GroupLabel>
                        <GroupButtons>
                            <ReviewActions slot={slot} title={title} compliance={compliance} latest={latest} />
                        </GroupButtons>
                    </ActionGroup>
                )}
                <ActionGroup>
                    <GroupLabel>Actions</GroupLabel>
                    <GroupButtons>{documentActions}</GroupButtons>
                </ActionGroup>
            </RowActions>
        </Row>
    );
};

const DocumentRow = ({ document, detail, children }) => (
    <Row>
        <div>
            <RowTitle>{document.name}</RowTitle>
            {detail && <RowDetail>{detail}</RowDetail>}
        </div>
        <RowActions>
            {children}
            <ViewButton document={document} />
            {!document.archived && <RemoveButton document={document} />}
        </RowActions>
    </Row>
);

const SlotCard = ({ title, slots, compliance }) => (
    <Card>
        <CardStrip>
            <StripTitle>{title}</StripTitle>
        </CardStrip>
        {slots.map((slot) => (
            <SlotRow key={slot.key} slot={slot} compliance={compliance} />
        ))}
    </Card>
);

const ComplianceHistory = ({ rows }) => {
    const [widths, setWidths] = useState(() => HISTORY_COLUMNS.map((column) => column.width));
    const columnsStyle = { gridTemplateColumns: widths.map((width) => `${width}px`).join(' ') };

    const startResize = (index) => (event) => {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = widths[index];

        const onMove = (moveEvent) => {
            const width = Math.max(MIN_COLUMN_WIDTH, startWidth + moveEvent.clientX - startX);
            setWidths((current) => current.map((value, i) => (i === index ? width : value)));
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    return (
        <Section>
            <SectionHeading title="Compliance History" />
            <Card>
                <HistoryScroll>
                    <HistoryHead style={columnsStyle}>
                        {HISTORY_COLUMNS.map((column, index) => (
                            <HeadCell key={column.label}>
                                {column.label}
                                <ResizeHandle
                                    role="separator"
                                    aria-orientation="vertical"
                                    aria-label={`Resize ${column.label}`}
                                    onPointerDown={startResize(index)}
                                />
                            </HeadCell>
                        ))}
                    </HistoryHead>
                    {rows.map((row) => {
                        const reviewedAt = format(new Date(row.reviewedAt), 'MM/dd/yyyy h:mm a');

                        return (
                            <HistoryGrid key={row.review_id} style={columnsStyle}>
                                <HistoryCell title={reviewedAt}>{reviewedAt}</HistoryCell>
                                <HistoryCell title={row.document}>{row.document}</HistoryCell>
                                <HistoryCell title={row.decision}>
                                    <StatusPill tone={row.tone}>{row.decision}</StatusPill>
                                </HistoryCell>
                                <HistoryCell title={row.note}>{row.note}</HistoryCell>
                                <HistoryCell title={row.reviewer}>{row.reviewer}</HistoryCell>
                            </HistoryGrid>
                        );
                    })}
                </HistoryScroll>
                {rows.length === 0 && <EmptyState>No compliance decisions yet.</EmptyState>}
            </Card>
        </Section>
    );
};

/**
 * The carrier's documents for the org. With `canReview` (the corporate admin's carrier page), each filled slot shows
 * its compliance decision with Approve / Deny, and a Compliance History lists the org's decisions.
 *
 * @param {{ canReview?: boolean }} props
 */
const InsuranceContracts = ({ canReview = false }) => {
    const corpAdmin = useContext(CorpAdminContext);
    const { client } = useContext(UserContext);
    const { organizationId, assignmentsByShipper, admin_clients } = corpAdmin.state;
    const { refetchCorpDocs } = corpAdmin.callbacks;
    const carrierId = client?.client_id;

    const { data, loading, error, refetch } = useQuery(GET_CARRIER_DOCUMENTS, {
        variables: { carrier_id: carrierId, organization_id: organizationId },
        skip: !carrierId || !organizationId,
        fetchPolicy: 'cache-and-network',
        onError: (err) => Sentry.captureException(err),
    });

    const clientsById = useMemo(
        () => Object.fromEntries((admin_clients || []).map((c) => [c.client_id, c])),
        [admin_clients]
    );

    const apolloClient = useApolloClient();
    const { data: reviewData, refetch: refetchReviews } = useQuery(GET_CARRIER_COMPLIANCE_REVIEWS, {
        variables: { carrier_id: carrierId, organization_id: organizationId },
        skip: !canReview || !carrierId || !organizationId,
        fetchPolicy: 'cache-and-network',
        onError: (err) => Sentry.captureException(err),
    });

    const compliance = useMemo(() => {
        if (!canReview || !reviewData) return null;

        const reviews = reviewData.contractor_compliance_reviews;
        const review = async ({ document, status, note }) => {
            try {
                // organization_id is only read by the emulator; deployed environments take it from the token.
                await post(REVIEW_CONTRACTOR_COMPLIANCE, {
                    carrier_id: carrierId,
                    organization_id: organizationId,
                    document_id: document.document_id,
                    status,
                    note,
                });
            } catch (err) {
                Sentry.captureException(err);
                corpAdmin.callbacks.setNotification({
                    severity: 'error',
                    message: errorMessage(err, 'Error saving the compliance decision'),
                });
                return false;
            }

            corpAdmin.callbacks.setNotification({
                severity: 'success',
                message: status === 'APPROVED' ? 'Document approved' : 'Document denied',
            });
            // A decision can change the contractor's partnership status, which the List View's Motor Carriers read.
            refetchReviews();
            apolloClient.refetchQueries({ include: [GET_ORG_CONTRACTOR_PARTNERSHIPS] }).catch(Sentry.captureException);
            return true;
        };

        return {
            decisionsByDocument: latestDecisionByDocument(reviews),
            historyRows: complianceHistoryRows(reviews, clientsById),
            review,
        };
    }, [
        apolloClient,
        canReview,
        carrierId,
        clientsById,
        corpAdmin.callbacks,
        organizationId,
        refetchReviews,
        reviewData,
    ]);

    const slots = useMemo(() => {
        if (!data) return null;

        const linkedShippersByCarrier = linkedShippersByCarrierFrom(assignmentsByShipper, clientsById);
        const linkedShippers = linkedShippersByCarrier[carrierId] ?? [];

        return documentSlotsFor({ documents: data.documents, linkedShippers, clientsById });
    }, [data, clientsById, assignmentsByShipper, carrierId]);

    if (loading && !data) {
        return (
            <EmptyState>
                <CircularProgress size={24} />
            </EmptyState>
        );
    }
    if (error && !data) return <EmptyState>Documents couldn't be loaded. Refresh to try again.</EmptyState>;
    if (!slots) return null;

    // DocumentUploadModal and RemoveButton refresh through refetchCorpDocs; this tab's own list must refresh too.
    const refetchDocuments = async () => {
        await Promise.all([refetchCorpDocs(), refetch()]);
    };
    const tabCorpAdmin = { ...corpAdmin, callbacks: { ...corpAdmin.callbacks, refetchCorpDocs: refetchDocuments } };

    return (
        <CorpAdminContext.Provider value={tabCorpAdmin}>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    padding: 24px 0;
                `}
            >
                <Section>
                    <SectionHeading title="Documents" />
                    <SlotCard title="Contracts" slots={slots.contracts} compliance={compliance} />
                    <SlotCard title="Insurance" slots={slots.insurance} compliance={compliance} />
                    <Card>
                        <CardStrip>
                            <StripTitle>Other Documents</StripTitle>
                            <StripNote>named at upload — a home for paperwork that fits no slot</StripNote>
                        </CardStrip>
                        {slots.others.map((document) => (
                            <DocumentRow key={document.document_id} document={document} />
                        ))}
                        <Row
                            css={css`
                                justify-content: flex-end;
                            `}
                        >
                            <UploadButton slot={OTHER_DOCUMENT_SLOT} secondary>
                                + Add document
                            </UploadButton>
                        </Row>
                    </Card>
                </Section>

                {slots.history.length > 0 && (
                    <Section>
                        <SectionHeading title="Document History" />
                        {slots.history.map((group) => (
                            <Card key={group.key}>
                                <CardStrip>
                                    <StripTitle>{group.label}</StripTitle>
                                </CardStrip>
                                {group.documents.map((document) => (
                                    <DocumentRow
                                        key={document.document_id}
                                        document={document}
                                        detail={`Effective Date: ${toDisplayDate(document.effectiveDate)}`}
                                    >
                                        {!document.archived && <StatusPill tone="on">On file</StatusPill>}
                                    </DocumentRow>
                                ))}
                            </Card>
                        ))}
                    </Section>
                )}

                {compliance && <ComplianceHistory rows={compliance.historyRows} />}
            </div>
        </CorpAdminContext.Provider>
    );
};

export default InsuranceContracts;
